import {ContentBlock, ErrorBoundary, ErrorDisplayComponent, Icon} from "@gplibraries/react/components";
import {type DealerInfo, useDealerSwitch, useLogger, useB2BDomains} from '@gplibraries/react/hooks';
import { Link } from 'react-router';

interface DealerInfoBlockProps {
  dealer: DealerInfo;
  user: any;
  associatedDealers: any[];
}


export default function DealerInfoBlock({ dealer, associatedDealers, user }: DealerInfoBlockProps) {

  const logger = useLogger();
  const switchDealer = useDealerSwitch();
  const { data: b2bDomains } = useB2BDomains();

  return (
    <ContentBlock title={'Your Dealership'} icon={<Icon glyph={'building-fill'}/>}>
      <ErrorBoundary
        fallback={<ErrorDisplayComponent message={'Your dealership information could not be loaded.'}/>}
        logger={logger}
      >

        <div className="dealership-info-block flex-col full">

          <div className="dealership-name">
            <h3>{dealer.dealer_name}</h3>
            <span>{dealer.dealer_number}</span>
          </div>

          <details>
            <summary>Basic Info <Icon glyph={'caret-right-fill'}/></summary>
            <table className={'nostyle full'}>
              <tbody>

              <tr>
                <th>Dealer Number:</th>
                <td>{dealer.dealer_number}</td>
              </tr>
              <tr>
                <th>Locale:</th>
                {dealer.locale == 'domestic' && <td>US and Canada</td>}
                {dealer.locale == 'export' && <td>Export</td>}
                {dealer.locale == 'misc' && <td>Miscellaneous</td>}
              </tr>
              {Object.keys(dealer.product_lines)
                // get non-null product lines
                .filter(key => dealer.product_lines[key])
                .sort()
                .map((line, index) => (index > 0)
                  ? (<tr key={line}><td>{dealer.product_lines[line].label}</td></tr>)
                  : (
                    <tr key={line}>
                      <th rowSpan={Object.keys(dealer.product_lines).filter(key => dealer.product_lines[key]).length}>Lines Carried:</th>
                      <td>{dealer.product_lines[line].label}</td>
                    </tr>
                  )
                )
              }
              </tbody>
            </table>

          </details>

          <details>
            <summary>Addresses <Icon glyph={'caret-right-fill'}/></summary>

            <table className={'nostyle full'}>
              <tbody>
              <tr>
                <th>Shipping Address:</th>
                <td>
                  {[dealer.address.shipping.address1,
                    dealer.address.shipping.address2,
                    dealer.address.shipping.address3,
                    `${dealer.address.shipping.city}, ${dealer.address.shipping.state} ${dealer.address.shipping.zip}`
                  ].join("\n")}
                </td>
              </tr>
              <tr>
                <th>Billing Address:</th>
                <td>
                  {[
                    dealer.address.billing.address1,
                    dealer.address.billing.address2,
                    dealer.address.billing.address3,
                    `${dealer.address.billing.city}, ${dealer.address.billing.state} ${dealer.address.billing.zip}`,
                  ].join('\n')}
                </td>
              </tr>
              </tbody>
            </table>

          </details>

          <details>
            <summary>Phone Numbers <Icon glyph={'caret-right-fill'}/>
            </summary>

            <table className={'nostyle full'}>
              <tbody>
              <tr>
                <th>Phone:</th>
                <td>{dealer.phone_number}</td>
              </tr>
              <tr>
                <th>Fax:</th>
                <td>{dealer.fax_number}</td>
              </tr>
              </tbody>
            </table>

          </details>

          <div className="flex-col">

            {(user?.isDealer() || (user?.isAdmin() && dealer?.dealer_number) || (user?.isCsUser() && dealer?.dealer_number)) &&
              <Link className={'button text-only bold'} to={'manage-employee-users'}>
                <Icon glyph={'people-fill'}/>Employee Users
              </Link>
            }

            {(dealer?.dealer_number && (user?.isDealer() || user?.isAdmin() || user?.isCsUser() || user?.isTm())) &&
            <Link className={'button text-only bold'} to={'/dealer_access/change_dealer_information.php'} reloadDocument={true}>
              <Icon glyph={'pencil-square'}/>Dealer Contacts/Emails
            </Link>
            }

            {(
              (user?.isDealer() && !b2bDomains?.includes(user?.get('email')?.split('@')[1])) ||
              (user?.isDealerEmployeeUser() && !b2bDomains?.includes(user?.get('gpmUsername')?.split('@')[1])) ||
              (user?.isCsUser() && dealer?.dealer_number) ||
              (user?.isAdmin() && dealer?.dealer_number)
            ) &&
              <Link className={'button text-only bold'} to={'change-password'}>
                <Icon glyph={'key'} style={{transform: 'scaleX(-1) rotate(45deg)'}}/>Change Password
              </Link>
            }

            {((associatedDealers?.length && user?.isDealer() === false) || user?.isCsUser() || user?.isAdmin()) &&
              <button className={'button text-only bold'} onClick={switchDealer}><Icon glyph={'arrow-repeat'}/>Switch Dealer</button>
            }

          </div>

        </div>
      </ErrorBoundary>
    </ContentBlock>
  );
}